import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'
import moment from 'moment'

import routes from '../../routers'
import Layout from '../../components/Layout'
import { apiError } from '../../api/account'
import {
  approveSubmission,
  createRequestLink,
  declineSubmission,
  getRequestLinks,
  getSubmissions,
  revokeRequestLink,
  submissionPhotoUrl,
} from '../../api/recipeRequests'

import '../PurchaseBook/styles.css'
import styles from './RecipeRequests.module.css'

const day = (d) => (d ? moment(d).format('MMM D') : '')

// Copy the link, or open the phone's share sheet / text message with it
const ShareButtons = ({ link }) => {
  const [copied, setCopied] = useState(false)
  const text = link.recipe_name
    ? `Could you send me your recipe for ${link.recipe_name}? It only takes a minute, no account needed: ${link.url}`
    : `Could you send me one of your recipes for our family cookbook? No account needed: ${link.url}`
  const copy = () =>
    navigator.clipboard
      ?.writeText(link.url)
      .then(() => {
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      })
      .catch(() => window.prompt('Copy this link', link.url))
  const share = () =>
    navigator.share
      ? navigator.share({ title: 'A recipe for the family', text }).catch(() => null)
      : (window.location.href = `sms:?&body=${encodeURIComponent(text)}`)
  return (
    <div className={styles.shareRow}>
      <input
        className={`cbInput ${styles.linkBox}`}
        value={link.url || ''}
        readOnly
        onFocus={(e) => e.target.select()}
      />
      <button type="button" className="cbSecondaryButton" onClick={copy}>
        {copied ? 'Copied' : 'Copy link'}
      </button>
      <button type="button" className="cbNextButton" onClick={share}>
        Text it
      </button>
    </div>
  )
}

const NewLink = ({ groups, onMade }) => {
  const [groupId, setGroupId] = useState(groups[0]?.id ? String(groups[0].id) : '')
  const [recipeName, setRecipeName] = useState('')
  const [made, setMade] = useState(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  // Groups can arrive after the page opens
  useEffect(() => {
    if (!groupId && groups[0]?.id) setGroupId(String(groups[0].id))
  }, [groups])

  const make = (e) => {
    e.preventDefault()
    setError('')
    setBusy(true)
    createRequestLink({ group_id: Number(groupId), recipe_name: recipeName || null })
      .then(({ data }) => {
        setMade(data)
        onMade()
      })
      .catch((err) => setError(apiError(err, 'We could not make the link. Please try again.')))
      .finally(() => setBusy(false))
  }

  if (!groups.length) {
    return (
      <section className="cbSection">
        <h2 className="cbSectionTitle">Ask for a recipe</h2>
        <p className="cbHint">
          First make a family group, then you can ask relatives for their recipes.
        </p>
        <Link className="cbNextButton" to={routes.manageFams}>
          Create a group
        </Link>
      </section>
    )
  }

  return (
    <section className="cbSection">
      <h2 className="cbSectionTitle">Ask for a recipe</h2>
      <p className="cbHint">
        Text a link to a relative. They send the recipe from their phone, no account needed, and you
        add it to the family’s recipes.
      </p>
      {made ? (
        <>
          <p className={styles.made}>
            Your link for {made.group_name}
            {made.recipe_name ? `: ${made.recipe_name}` : ''}
          </p>
          <ShareButtons link={made} />
          <button
            type="button"
            className={`cbTextLink ${styles.textButton}`}
            onClick={() => setMade(null)}
          >
            Ask for another recipe
          </button>
        </>
      ) : (
        <form className={styles.newForm} onSubmit={make}>
          <label className={styles.field}>
            For which family group
            <select
              className="cbInput"
              value={groupId}
              onChange={(e) => setGroupId(e.target.value)}
            >
              {groups.map((g) => (
                <option key={g.id} value={String(g.id)}>
                  {g.name}
                </option>
              ))}
            </select>
          </label>
          <label className={styles.field}>
            Which recipe (optional)
            <input
              className="cbInput"
              value={recipeName}
              onChange={(e) => setRecipeName(e.target.value)}
              maxLength={100}
              placeholder="Aunt Linda’s pecan pie"
            />
          </label>
          {error ? <p className="cbError">{error}</p> : null}
          <button type="submit" className="cbNextButton" disabled={busy || !groupId}>
            {busy ? 'Making the link…' : 'Make a link'}
          </button>
        </form>
      )}
    </section>
  )
}

const Submission = ({ s, onDone }) => {
  const navigate = useNavigate()
  const [busy, setBusy] = useState('')
  const [error, setError] = useState('')
  const approve = () => {
    setBusy('approve')
    setError('')
    approveSubmission(s.id)
      .then(({ data }) => navigate(`${routes.addRecipe}?id=${data.recipe_id}`))
      .catch((err) => {
        setBusy('')
        setError(apiError(err, 'We could not add this recipe. Please try again.'))
      })
  }
  const decline = () => {
    if (!window.confirm(`Let “${s.recipe_name}” go? ${s.contributor_name} won’t be told.`)) return
    setBusy('decline')
    declineSubmission(s.id)
      .then(onDone)
      .catch((err) => {
        setBusy('')
        setError(apiError(err, 'Something went wrong. Please try again.'))
      })
  }
  return (
    <article className={styles.submission}>
      <div className="cbSectionHead">
        <div>
          <h3 className={styles.recipeName}>{s.recipe_name}</h3>
          <p className="cbMuted">
            From {s.contributor_name} ({s.contributor_email}) for {s.group_name},{' '}
            {day(s.created_at)}
          </p>
        </div>
      </div>
      {s.photos?.length ? (
        <div className={styles.photos}>
          {s.photos.map((p, i) => (
            <a key={p} href={submissionPhotoUrl(p)} target="_blank" rel="noopener noreferrer">
              <img src={submissionPhotoUrl(p)} alt={`Recipe card photo ${i + 1}`} />
            </a>
          ))}
        </div>
      ) : null}
      {s.ingredients_text ? (
        <>
          <p className="cbLabel">Ingredients</p>
          <p className={styles.text}>{s.ingredients_text}</p>
        </>
      ) : null}
      {s.steps_text ? (
        <>
          <p className="cbLabel">Steps</p>
          <p className={styles.text}>{s.steps_text}</p>
        </>
      ) : null}
      {s.notes ? <p className={styles.text}>{s.notes}</p> : null}
      {error ? <p className="cbError">{error}</p> : null}
      {s.status === 'pending' ? (
        <div className={styles.actions}>
          <button type="button" className="cbNextButton" onClick={approve} disabled={!!busy}>
            {busy === 'approve' ? 'Adding…' : 'Add to family recipes'}
          </button>
          <button type="button" className="cbSecondaryButton" onClick={decline} disabled={!!busy}>
            Let it go
          </button>
        </div>
      ) : null}
      {s.status === 'approved' && s.recipe_id ? (
        <Link className="cbTextLink" to={`${routes.addRecipe}?id=${s.recipe_id}`}>
          Open the recipe
        </Link>
      ) : null}
    </article>
  )
}

const RecipeRequests = () => {
  const user = useSelector((state) => state.user?.data)
  const groups = useSelector((state) => state.groups?.data) || []
  const [tab, setTab] = useState('pending')
  const [submissions, setSubmissions] = useState(null)
  const [links, setLinks] = useState([])

  const loadSubmissions = () =>
    getSubmissions(tab)
      .then(({ data }) => setSubmissions(data))
      .catch(() => setSubmissions([]))
  const loadLinks = () =>
    getRequestLinks()
      .then(({ data }) => setLinks(data))
      .catch(() => setLinks([]))

  useEffect(() => {
    setSubmissions(null)
    loadSubmissions()
  }, [tab])
  useEffect(() => {
    loadLinks()
  }, [])

  const revoke = (link) => {
    if (
      !window.confirm('Stop this link? Anyone who has it won’t be able to send a recipe with it.')
    )
      return
    revokeRequestLink(link.id)
      .then(loadLinks)
      .catch(() => null)
  }

  const enabled = !!user?.features?.recipe_requests
  return (
    <Layout title="Recipe requests">
      <div className={`cbPage ${styles.page}`}>
        <div className="cbIntro">
          <h1 className="cbTitle">Recipe requests</h1>
        </div>

        {enabled ? <NewLink groups={groups} onMade={loadLinks} /> : null}

        <section className="cbSection">
          <div className={styles.tabs} role="tablist">
            {[
              ['pending', 'Waiting for you'],
              ['approved', 'Added'],
              ['declined', 'Let go'],
            ].map(([key, label]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={tab === key}
                className={`${styles.tab} ${tab === key ? styles.tabOn : ''}`}
                onClick={() => setTab(key)}
              >
                {label}
              </button>
            ))}
          </div>
          {!submissions ? <p className="cbMuted">Loading…</p> : null}
          {submissions && !submissions.length ? (
            <p className="cbMuted">
              {tab === 'pending'
                ? 'Nothing waiting. Recipes your family sends will show up here.'
                : 'None yet.'}
            </p>
          ) : null}
          {submissions?.map((s) => (
            <Submission key={s.id} s={s} onDone={loadSubmissions} />
          ))}
        </section>

        {links.length ? (
          <section className="cbSection">
            <h2 className="cbSectionTitle">Your links</h2>
            {links.map((l) => (
              <div key={l.id} className={styles.linkRow}>
                <div>
                  <b>{l.recipe_name || 'Any recipe'}</b>
                  <small>
                    {l.group_name}, made {day(l.created_at)}
                    {l.submissions ? `, ${l.submissions} sent` : ''}
                  </small>
                </div>
                <ShareButtons link={l} />
                <button
                  type="button"
                  className={`cbTextLink ${styles.textButton}`}
                  onClick={() => revoke(l)}
                >
                  Stop this link
                </button>
              </div>
            ))}
          </section>
        ) : null}
      </div>
    </Layout>
  )
}

export default RecipeRequests
